import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SearchControlService } from '@app/pages/map/controls/services/search-control.service';
import { tourStation, tourSteps, tourSteps2 } from '@app/shared/utils/tour';
import { TourService } from 'ngx-ui-tour-md-menu';
import { LocalStorageService } from './local-storage.service';
import { AuthService, UserSignal } from './auth.service';
import { UserService } from './user.service';
import { firstValueFrom } from 'rxjs';
import { PublicMetadataInterface } from './user.interface';

const STORAGE_KEY = 'onboarding-tour-completed';

@Injectable({
  providedIn: 'root',
})
export class OnboardingTourService {
  user: UserSignal;
  userMetadata: PublicMetadataInterface;

  constructor(
    private tourService: TourService,
    private searchControlService: SearchControlService,
    private router: Router,
    private localStorageService: LocalStorageService,
    private authService: AuthService,
    private userService: UserService,
  ) {}

  init() {
    this.tourService.initialize(tourSteps, {
      enableBackdrop: true,
      duplicateAnchorHandling: 'registerLast',
      endBtnTitle: 'Next',
      showProgress: false,
      stepDimensions: {
        minWidth: '350px',
      },
    });
    const firstObs = this.tourService.end$.subscribe(() => {
      this.markAsCompleted();
      if (this.searchControlService.userLocation) {
        this.searchControlService.map.setView(
          {
            lat: this.searchControlService.userLocation.lat,
            lng: this.searchControlService.userLocation.lng,
          },
          14,
        );
      }
    });
    this.tourService.stepHide$.subscribe(({ step }) => {
      if (step.anchorId === 'app-search') {
        this.searchControlService.map.setView(
          [tourStation.latitude, tourStation.longitude],
          14,
        );
      }
      if (step.anchorId === 'marker-tour-step') {
        const sub = this.tourService.end$.subscribe(() => {
          this.router.navigate(['/station', tourStation.stationId]).then(() => {
            firstObs.unsubscribe();
            this.tourService.initialize(tourSteps2, {
              enableBackdrop: true,
              duplicateAnchorHandling: 'registerLast',
              showProgress: false,
              stepDimensions: {
                minWidth: '350px',
              },
            });
            this.tourService.end$.subscribe(() => {
              this.markAsCompleted();
              this.router.navigate(['/']);
              if (this.searchControlService.userLocation) {
                this.searchControlService.map.setView(
                  {
                    lat: this.searchControlService.userLocation.lat,
                    lng: this.searchControlService.userLocation.lng,
                  },
                  14,
                );
              }
            });

            this.tourService.start();
          });
          sub.unsubscribe();
        });

        this.tourService.end();
      }
    });

    this.tourService.start();
  }

  async getMetadata() {
    try {
      if (this.authService.isAuthenticated(this.user?.permission)) {
        return await firstValueFrom(this.userService.getMetadataById(this.user.uid));
      }
    } catch {
      //
    }

    return {
      displayName: this.user?.displayName,
      userPreferences: {
        onboardingCompleted: false,
        vehicles: [],
      },
    };
  }

  async start() {
    // fetch user metadata
    this.user = await firstValueFrom(this.authService.getUserObject());
    this.userMetadata = await this.getMetadata();

    // only show this in the homepage
    if (window.location.pathname !== '/') return;

    // if the user has already completed the tour, don't show it again
    // use localStorage to store the state
    // for logged in users, check if the user has completed the tour
    const userCompleted = this.userMetadata?.userPreferences?.onboardingCompleted;
    const localCompleted = this.localStorageService.getItem(STORAGE_KEY) === 'true';
    if (localCompleted && !userCompleted) this.markAsCompleted();
    if (userCompleted || localCompleted) return;

    this.init();
  }

  async markAsCompleted() {
    this.localStorageService.setItem(STORAGE_KEY, 'true');

    // if logged in
    if (this.user?.uid) {
      const userMetadata = await this.getMetadata();

      this.userService
        .updateMetadata(this.user.uid, {
          ...userMetadata,
          userPreferences: {
            ...userMetadata.userPreferences,
            onboardingCompleted: true,
          },
        })
        .subscribe();
    }
  }
}
